import { defineStore } from 'pinia';

// One Pinia store keyed by list name. Replaces the per-list `update*`
// mutations + `store.subscribe(...includes(...))` matching that p-select
// and p-advanced-select used to do — that pattern crossed wires when a
// dataType was a substring of another (e.g. "accounts" ⊂ "chartOfAccounts").
//
// Components read with `useSelectListsStore().getList(name)` or directly
// off `store.lists[name]`. SignalR clears via `clearList(name)` and the
// dataContext refetches; reactivity then fans out to every consumer of
// that key without any extra wiring.
// Static lists seeded at app boot — these used to live as initial Vuex state.
// They never change at runtime so they sit in Pinia from the start, which means
// the dataContext finds them on first read and never needs to fetch or fall
// back to Vuex for them.
const STATIC_LISTS = {
    poStatus: ['Proposed', 'Open', 'Closed'],
    invoiceStatus: ['Open', 'Posted', 'Cancelled', 'Paid'],
    courseLevels: [
        { value: 0, text: '' },
        { value: 1, text: '1' },
        { value: 2, text: '2' },
        { value: 3, text: '3' },
        { value: 4, text: '4' },
        { value: 5, text: '5' }
    ],
    workStatus: ['Not worked', 'Work', 'Worked', 'Rework', 'In Process', 'Check', 'Other'],
    yesNoOptions: [
        { value: true, text: 'Yes' },
        { value: false, text: 'No' }
    ],
    yesNoNullOptions: [
        { value: true, text: 'Yes' },
        { value: false, text: 'No' },
        { value: null, text: '' }
    ],
    addReplaceOptions: [
        { value: true, text: 'Add' },
        { value: false, text: 'Replace' }
    ],
    arAccountActionOptions: [
        { value: true, text: 'Link' },
        { value: false, text: 'Unlink' }
    ],
    isActiveOptions: [
        { value: true, text: 'Active' },
        { value: false, text: 'Inactive' }
    ],
    ShippingMethods: [
        { value: 1, text: 'Price' },
        { value: 2, text: 'Item' }
    ],
    openCompletedOptions: [
        { value: 'Open', text: 'Open' },
        { value: 'Completed', text: 'Completed' }
    ],
    openOption: [{ value: 'Open', text: 'Open' }],
    dollarOrPercentage: [
        { value: 'Dollar', text: 'Dollar' },
        { value: 'Percentage', text: 'Percentage' }
    ],
    buybackToSubtractOptions: [
        { value: 'None', text: 'None' },
        { value: 'Estimated', text: 'Estimated' },
        { value: 'Actual', text: 'Actual' }
    ],
    returnedRentalsToSubtractOptions: [
        { value: 'None', text: 'None' },
        { value: 'Estimated', text: 'Estimated' },
        { value: 'Actual', text: 'Actual' }
    ],
    supplierReturnPolicies: [
        { value: 1, text: 'Return Permission Required' },
        { value: 2, text: 'No Permission Required' },
        { value: 3, text: 'No Returns' }
    ],
    debitOrCreditOptions: [
        { value: false, text: 'Credit' },
        { value: true, text: 'Debit' }
    ]
};

export const useSelectListsStore = defineStore('selectLists', {
    state: () => ({
        lists: { ...STATIC_LISTS }
    }),
    getters: {
        getList: state => name => state.lists[name] ?? [],
        // Lookup helpers — these mirror the old Vuex getter signatures so
        // call sites can swap `this.$store.getters.getXById(id)` for
        // `useSelectListsStore().getXById(id)` with no other change.
        getLocationById: state => id => state.lists.locations?.find(x => x.value === id),
        getStateById: state => id => state.lists.states?.find(x => x.value === id),
        getSupplierById: state => id => state.lists.suppliers?.find(x => x.value === id),
        getTermById: state => id => state.lists.terms?.find(x => x.value === id),
        getTenderById: state => id => state.lists.tenders?.find(x => x.value === id),
        getBuybackBuyerById: state => id => state.lists.buybackBuyers?.find(x => x.value === id),
        getBuybackBuyerGroupById: state => id => state.lists.buybackBuyerGroups?.find(x => x.value === id),
        // Only matches active types - inactive ones come back disabled from the api and are
        // not a valid default for a newly added course request product.
        getActiveCourseRequestBookStatusByName: state => name =>
            state.lists.courseRequestBookStatuses?.find(x => x.text === name && !x.disabled)
    },
    actions: {
        setList(name, data) {
            this.lists[name] = data;
        },
        // Append an option to a list, replacing the array reference so reactive
        // consumers (p-advanced-select's pinedOptions watcher, etc.) pick it up.
        // Used to surface a just-created record (e.g. a new brand) immediately,
        // before SignalR refreshes the list from the DB.
        addToList(name, item) {
            const current = Array.isArray(this.lists[name]) ? this.lists[name] : [];
            this.lists[name] = [...current, item];
        },
        clearList(name) {
            this.lists[name] = [];
        }
    }
});
